import Joi from 'joi';

import {
  emailRegex,
  lightWeightDashboardPasswordRegex,
  passwordRegex,
  urlRegex,
  userFirstAndLastNameRegex,
} from './global';

export const passwordSchema = Joi.object({
  password: Joi.string().regex(passwordRegex).max(300).required().messages({
    'string.pattern.base':
      'Password should contain minimum 6 Characters with minimum 1 Uppercase Character and 1 Number',
    'any.required': 'Please enter a valid password',
    'string.max': "Password can't be more than 300 characters long",
  }),
});

export const changePasswordSchema = Joi.object({
  password: Joi.string().regex(passwordRegex).max(300).required().messages({
    'string.pattern.base':
      'Password should contain minimum 6 Characters with minimum 1 Uppercase Character and 1 Number',
    'any.required': 'Please enter a valid password',
    'string.max': "Password can't be more than 300 characters long",
  }),
  oldPassword: Joi.string().required().max(300).messages({
    'string.pattern.base':
      'Password should contain minimum 6 Characters with minimum 1 Uppercase Character and 1 Number',
    'any.required': 'Please enter a valid password',
    'string.max': "Password can't be more than 300 characters long",
  }),
});

export const uploadResourceLinkSchema = Joi.object({
  resourceName: Joi.string().required().min(2).max(100).messages({
    'any.required': 'Name is required',
    'string.min': 'Please be more descriptive',
    'string.max': 'Please be less descriptive',
  }),
  resourceData: Joi.string().uri().required().messages({
    'any.required': 'Link cannot be empty',
    'string.uri': 'Invalid Link',
  }),
  description: Joi.string().required().min(5).max(50).messages({
    'any.required': 'Description is required',
    'string.min': 'Please be more descriptive',
    'string.max': 'Please be less descriptive',
  }),
  resourceType: Joi.string().allow('media', 'link').required().messages({
    'any.required': 'Resource Type cannot be empty',
  }),
  formId: Joi.string().required(),
});

export const userFirstNameSchema = Joi.string().required().min(2).max(100).regex(userFirstAndLastNameRegex).messages({
  'string.pattern.base': 'First Name can only include alphabets',
  'string.min': 'First Name should contain minimum 2 characters',
  'string.max': 'First Name can contain maximum 100 characters',
  'any.required': 'First Name is required',
  'string.empty': 'First Name Cannot be empty',
});

export const userLastNameSchema = Joi.string()
  .required()
  .allow('')
  .min(2)
  .max(100)
  .regex(userFirstAndLastNameRegex)
  .messages({
    'string.pattern.base': 'Last Name can only include alphabets',
    'string.min': 'Last Name should contain minimum 2 characters',
    'string.max': 'Last Name can contain maximum 100 characters',
    'any.required': 'Last Name is required',
  });

export const userProfileNameUpdateSchema = Joi.object({
  firstName: userFirstNameSchema,
  lastName: userLastNameSchema,
});

export const brandNameSchema = Joi.string().required().min(2).max(100).regex(userFirstAndLastNameRegex).messages({
  'string.pattern.base': 'Brand Name can only include alphabets',
  'string.min': 'Brand Name should contain minimum 2 characters',
  'string.max': 'Brand Name can contain maximum 100 characters',
  'any.required': 'Brand Name is required',
  'string.empty': 'Brand Name Cannot be empty',
});

export const brandNameUpdateSchema = Joi.object({
  brandName: brandNameSchema,
});

export const aovFactorSchema = Joi.number().valid(0.5, 0.7, 0.9, 1.2, 1.4).positive().required().messages({
  'any.required': 'AOV Factor is a required Field',
  'number.base': 'AOV Factor is supposed to be a number',
  'number.positive': 'AOV Factor has to be a positive value',
  'any.only': 'Invalid AOV Factor',
});

export const aovFactorUpdateSchema = Joi.object({
  aov_factor: aovFactorSchema,
});

export const surveyForm1Validation = Joi.object({
  website: Joi.string().regex(urlRegex).messages({
    'string.pattern.base': 'Please enter an actual website',
    'string.empty': 'Website url is required.',
  }),
  role: Joi.string().trim().required().min(3).max(500).messages({
    'any.required': 'Please enter your role',
    'string.min': 'Please elaborate your role',
    'string.max': 'Please reduce the length of role',
    'string.empty': 'Please enter your role',
  }),
});

export const surveyForm2Validation = Joi.object({
  whySaral: Joi.string().trim().required().min(10).max(5000).messages({
    'any.required': 'Tell us more than that...',
    'string.min': 'Tell us more than that...',
    'string.max': 'Tell us less than that...',
    'string.empty': 'Tell us more than that...',
  }),
  saralReferal: Joi.string().trim().required().min(10).max(500).messages({
    'any.required': 'Tell us more than that...',
    'string.min': 'Tell us more than that...',
    'string.max': 'Tell us less than that...',
    'string.empty': 'Tell us more than that...',
  }),
});

export const surveyForm3Validation = Joi.object({
  revenue: Joi.string().required().valid('$0', '$300K - $1M', '$1M - $3M', '$3M - $10M', '$10M+').messages({
    'any.required': 'We need to know your revenue for customized experience.',
    'string.empty': 'We need to know your revenue for customized experience.',
    'string.valid': 'Invalid option',
  }),
  objective: Joi.string()
    .required()
    .valid('Seeding / Gifting', 'Paid Promotions', 'I need UGC', 'Ambassador Program', 'Still figuring out')
    .messages({
      'any.required': 'We need to know your primary objective for customized experience.',
      'string.empty': 'We need to know your primary objective for customized experience.',
      'string.valid': 'Invalid option',
    }),
  teamSize: Joi.string().required().valid('1-10', '10-25', '25-50', '50-100', '100+').messages({
    'any.required': 'We need to know your team size for customized experience.',
    'string.empty': 'We need to know your team size for customized experience.',
    'string.valid': 'Invalid option',
  }),
});

export const surveyForm1V2Validation = Joi.object({
  winForBrand: Joi.string().trim().required().min(10).max(5000).messages({
    'any.required': 'Tell us more than that...',
    'string.min': 'Tell us more than that...',
    'string.max': 'Tell us less than that...',
    'string.empty': 'Tell us more than that...',
  }),
  roadblocks: Joi.string().trim().required().min(10).max(5000).messages({
    'any.required': 'Tell us more than that...',
    'string.min': 'Tell us more than that...',
    'string.max': 'Tell us less than that...',
    'string.empty': 'Tell us more than that...',
  }),
});

export const surveyForm2V2Validation = Joi.object({
  objective: Joi.string()
    .required()
    .valid('Seeding / Gifting', 'Paid Promotions', 'I need UGC', 'Ambassador Program', 'Still figuring out')
    .messages({
      'any.required': 'We need to know your primary objective for customized experience.',
      'string.empty': 'We need to know your primary objective for customized experience.',
      'string.valid': 'Invalid option',
    }),
});

export const bulkEditInfluencerDetailsValidation = Joi.object({
  name: Joi.string().trim().required().messages({
    'any.required': "Influencer's name is required.",
    'string.empty': "Influencer's name is required.",
  }),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .trim()
    .optional()
    .messages({ 'string.email': 'Input should be valid email.' }),
  instagram: Joi.string()
    .trim()
    .regex(/instagram/i)
    .optional()
    .messages({
      'string.empty': 'Instagram link is required.',
      'string.pattern.base': 'Invalid instagram url.',
    }),
  tiktok: Joi.string()
    .trim()
    .regex(/tiktok/i)
    .optional()
    .messages({
      'string.empty': 'Tiktok link is required.',
      'string.pattern.base': 'Invalid tiktok url.',
    }),
  youtube: Joi.string()
    .trim()
    .regex(/youtube/i)
    .optional()
    .messages({
      'string.empty': 'Youtube link is required.',
      'string.pattern.base': 'Invalid youtube url.',
    }),
});

export const customerCommission = Joi.object({
  type: Joi.string().valid('percentage', 'flat_rate'),
  value: Joi.when('type', {
    is: Joi.string().valid('percentage'),
    then: Joi.number().required().max(100).min(0).messages({
      'number.base': 'Commission value is required.',
      'number.max': "Commission percent can't be more than 100",
      'number.min': "Commission percent can't less than 0",
    }),
    otherwise: Joi.number().required().messages({ 'number.base': 'Commission value is required.' }),
  }),
});

export const customDiscountCodeValidation = Joi.object({
  discountValue: Joi.number().required().max(100).min(0).messages({
    'number.base': 'Discount Value is required.',
    'number.max': "Discount Value can't be more than 100%",
    'number.min': "Discount Value can't less than 0",
  }),
});

export const contentSubmissionDashboardSchema = Joi.object({
  influencerName: Joi.string().required().messages({
    'any.required': 'Influencer name is required',
  }),
  email: Joi.string().regex(emailRegex).required().messages({
    'any.required': 'Email is required',
    'string.pattern.base': 'Enter a valid email address',
  }),
  password: Joi.string().regex(lightWeightDashboardPasswordRegex).max(300).required().messages({
    'string.pattern.base': 'Password should contain minimum 6 Characters',
    'any.required': 'Please enter a valid password',
    'string.max': "Password can't be more than 300 characters long",
  }),
});
